import { AnimationEvent } from '@angular/animations';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  InjectionToken,
  Input,
  NgZone,
  Output,
  QueryList,
} from '@angular/core';
import { filter, merge, startWith, Subject, take, takeUntil } from 'rxjs';

import { DrawerComponent } from './drawer.component';

export const MAT_DRAWER_CONTAINER = new InjectionToken('MAT_DRAWER_CONTAINER');

@Component({
    selector: 'lui-drawer-container',
    templateUrl: './drawer-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DrawerContainerComponent implements AfterContentInit {
  /** All drawers in the container. Includes drawers from inside nested containers. */
  @ContentChildren(DrawerComponent, {
    // We need to use `descendants: true`, because Ivy will no longer match
    // indirect descendants if it's left as false.
    descendants: true,
  })
  _allDrawers!: QueryList<DrawerComponent>;

  @ContentChildren(DrawerComponent)
  _drawers = new QueryList<DrawerComponent>();

  // @ContentChild(DrawerContentComponent) _content!: DrawerContentComponent;
  // @ViewChild(DrawerContentComponent) _userContent!: DrawerContentComponent;

  /** The drawer child with the `start` position. */
  get start(): DrawerComponent | null {
    return this._start;
  }

  /** The drawer child with the `end` position. */
  get end(): DrawerComponent | null {
    return this._end;
  }

  /** The drawer at the start/end/bottom position, independent of direction. */
  private _start!: DrawerComponent | null;
  private _end!: DrawerComponent | null;

  /**
   * Whether the drawer container should have a backdrop while one of the sidenavs is open.
   * If explicitly set to `true`, the backdrop will be enabled for drawers in the `side`
   * mode as well.
   */
  @Input()
  get hasBackdrop(): boolean {
    if (this._backdropOverride == null) {
      return (
        !this._start ||
        this._start.mode !== 'side' ||
        !this._end ||
        this._end.mode !== 'side'
      );
    }

    return this._backdropOverride;
  }
  set hasBackdrop(value: BooleanInput) {
    this._backdropOverride =
      value == null ? null : coerceBooleanProperty(value);
  }
  _backdropOverride!: boolean | null;

  get _isBackdropFixed(): boolean {
    return this.coverClass;
  }

  /** Event emitted when the drawer backdrop is clicked. */
  @Output() readonly backdropClick: EventEmitter<void> =
    new EventEmitter<void>();

  _contentMargins: { left: number | null; right: number | null } = {
    left: null,
    right: null,
  };

  readonly _contentMarginChanges = new Subject<{
    left: number | null;
    right: number | null;
  }>();

  @HostBinding('class') modelClass = 'lui-drawer-container';

  @HostBinding('class.lui-drawer-container-cover') coverClass = false;

  /** Emits when the component is destroyed. */
  private readonly _destroyed = new Subject<void>();

  constructor(
    private _element: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone
  ) {}

  ngAfterContentInit(): void {
    this._drawers.changes.pipe(startWith(null)).subscribe(() => {
      this._validateDrawers();

      this._drawers.forEach((drawer: DrawerComponent) => {
        this._watchDrawerToggle(drawer);
        this._watchDrawerPosition(drawer);
        this._watchDrawerMode(drawer);
        this._watchPanelChanges(drawer);
      });

      if (
        !this._drawers.length ||
        this._isDrawerOpen(this._start) ||
        this._isDrawerOpen(this._end)
      ) {
        this.updateContentMargins();
      }
      this._changeDetectorRef.markForCheck();
    });
  }

  /** Validate the state of the drawer children components. */
  private _validateDrawers() {
    this._start = this._end = null;

    // Ensure that we have at most one start and one end drawer.
    this._drawers.forEach((drawer) => {
      if (drawer.position == 'end') {
        if (this._end != null) {
          throw new Error('duplicate end drawer');
        }
        this._end = drawer;
      }
      if (drawer.position == 'start') {
        if (this._start != null) {
          throw new Error('duplicate start drawer');
        }
        this._start = drawer;
      }
    });

    this.coverClass =
      this._drawers.filter((drawer) => drawer.mode === 'cover').length > 0;
  }

  _onBackdropClicked() {
    this.backdropClick.emit();
    this._closeModalDrawersViaBackdrop();
  }

  _closeModalDrawersViaBackdrop() {
    // Close all open drawers where closing is not disabled and the mode is not `side`.
    [this._start, this._end]
      .filter(
        (drawer) =>
          drawer && !drawer.disableClose && this._canHaveBackdrop(drawer)
      )
      .forEach((drawer) => {
        if (drawer) {
          drawer._closeViaBackdropClick();
        }
      });
  }

  _isShowingBackdrop(): boolean {
    return (
      (this._isDrawerOpen(this._start) && this._canHaveBackdrop(this._start)) ||
      (this._isDrawerOpen(this._end) && this._canHaveBackdrop(this._end))
    );
  }

  private _canHaveBackdrop(drawer: DrawerComponent): boolean {
    return drawer.mode !== 'side' || !!this._backdropOverride;
  }

  private _isDrawerOpen(
    drawer: DrawerComponent | null
  ): drawer is DrawerComponent {
    return drawer != null && drawer.opened;
  }

  /**
   * Subscribes to drawer events in order to set a class on the main container element when the
   * drawer is open and the backdrop is visible. This ensures any overflow on the container element
   * is properly hidden.
   */
  private _watchDrawerToggle(drawer: DrawerComponent): void {
    drawer._animationStarted
      .pipe(
        filter((event: AnimationEvent) => event.fromState !== event.toState),
        takeUntil(this._drawers.changes)
      )
      .subscribe((event: AnimationEvent) => {
        // Set the transition class on the container so that the animations occur. This should not
        // be set initially because animations should only be triggered via a change in state.
        this.updateContentMargins();

        if (event.toState !== 'open-instant') {
          this._element.nativeElement.classList.add(
            'lui-drawer-animation-ready'
          );
        }

        this._changeDetectorRef.markForCheck();
      });

    if (drawer.mode !== 'side') {
      drawer.openedChange
        .pipe(takeUntil(this._drawers.changes))
        .subscribe(() => this._setContainerClass(drawer.opened));
    }
  }

  private _watchDrawerPosition(drawer: DrawerComponent): void {
    if (!drawer) {
      return;
    }
    // NOTE: We need to wait for the microtask queue to be empty before validating,
    // since both drawers may be swapping positions at the same time.
    drawer.positionChanged
      .pipe(takeUntil(this._drawers.changes))
      .subscribe(() => {
        this._ngZone.onMicrotaskEmpty.pipe(take(1)).subscribe(() => {
          this._validateDrawers();
          this.updateContentMargins();

          this._changeDetectorRef.markForCheck();
        });
      });
  }

  /** Subscribes to changes in drawer mode so we can run change detection. */
  private _watchDrawerMode(drawer: DrawerComponent): void {
    if (drawer) {
      drawer._modeChanged
        .pipe(takeUntil(merge(this._drawers.changes, this._destroyed)))
        .subscribe(() => {
          this.updateContentMargins();
          this._changeDetectorRef.markForCheck();
        });
    }
  }

  /** Subscribes to changes in drawer mode so we can run change detection. */
  private _watchPanelChanges(drawer: DrawerComponent): void {
    if (drawer) {
      drawer._panelChanged
        .pipe(takeUntil(merge(this._drawers.changes, this._destroyed)))
        .subscribe(() => {
          this.updateContentMargins();
          this._changeDetectorRef.markForCheck();
        });
    }
  }

  /** Toggles the 'mat-drawer-opened' class on the main 'mat-drawer-container' element. */
  private _setContainerClass(isAdd: boolean): void {
    const classList = this._element.nativeElement.classList;
    const className = 'mat-drawer-container-has-open';

    if (isAdd) {
      classList.add(className);
    } else {
      classList.remove(className);
    }
  }

  /**
   * Recalculates and updates the inline styles for the content. Note that this should be used
   * sparingly, because it causes a reflow.
   */
  updateContentMargins(): void {
    const verticalPanelWidth = 40;

    // 1. For drawers in `over` mode, they don't affect the content.
    // 2. For drawers in `side` mode they should shrink the content. We do this by adding to the
    //    start margin (for start drawer) or end margin (for end the drawer).
    // 3. For drawers in `push` mode the should shift the content without resizing it. We do this by
    //    adding to the start or end margin and simultaneously subtracting the same amount of
    //    margin from the other side.
    let start = 0;
    let end = 0;

    if (this._start && this._start.mode != 'over') {
      if (this._start.usePanel && !this._start.opened) {
        start += verticalPanelWidth;
      }

      if (this._start.opened) {
        if (this._start.mode == 'side') {
          start += this._start._getWidth();
        } else if (this._start.mode == 'push') {
          const width = this._start._getWidth();
          start += width;
          end -= width;

          if (this._start.usePanel) {
            end -= verticalPanelWidth;
          }
        }
      }
    }

    if (this._end && this._end.mode != 'over') {
      if (this._end.usePanel && !this._end.opened) {
        end += verticalPanelWidth;
      }

      if (this._end.opened) {
        if (this._end.mode == 'side') {
          end += this._end._getWidth();
        } else if (this._end.mode == 'push') {
          const width = this._end._getWidth();
          end += width;
          start -= width;

          if (this._end.usePanel) {
            start -= verticalPanelWidth;
          }
        }
      }
    }

    // If either `end` or `start` is zero, don't set a style to the element. This
    // allows users to specify a custom size via CSS class in SSR scenarios where the
    // measured widths will always be zero. Note that we reset to `null` here, rather
    // than below, in order to ensure that the types in the `if` below are consistent.
    start = start || 0;
    end = end || 0;

    if (
      start !== this._contentMargins.left ||
      end !== this._contentMargins.right
    ) {
      this._contentMargins = { left: start, right: end };

      // Pull back into the NgZone since in some cases we could be outside. We need to be careful
      // to do it only when something changed, otherwise we can end up hitting the zone too often.
      this._ngZone.run(() =>
        this._contentMarginChanges.next(this._contentMargins)
      );
    }
  }
}
